<template>
  <ion-page id="selectvoices-page">
    <ion-content>

      <div v-if="showState == SHOW_EXPLAINTOUR">
        <ExplainTour :screen="explainTourScreen"
                      :wordsSpoken="explainTourWordsSpoken"
                      :wordsSpokenTrigger="explainTourWordsSpokenTrigger"
                    @toolbar-click="explainTourToolbarClick" />
      </div>
      <div v-show="showState == SHOW_VOICES" :class="{ 'text-dark' : isDark,'text-light' : !isDark }">
        <TopLine :style="styleTopline" :title="selectVoicesTitle" :microphoneIsMuted="microphoneIsMuted" @topline-click="toplineClick"/>
        <ion-list id="select-voices" :style="styleVoicesAvailable">
           <ion-item v-if="isLoading">
            <ion-label>
              <h1>Loading Available Voice List</h1>
            </ion-label>
          </ion-item>
          <ion-item>
            <ion-label color="danger">
              {{ translateEnglishWords("To Hear Voice : Say ' command voice # ' as in ' command voice 5 '") }}
            </ion-label>
          </ion-item>

          <ion-item v-for="item in voicesItems" v-bind:key="item.index" @click="onClick(item.index)"
                 :class="{
                  'selected-text-dark' : item.selected && isDark,
                  'selected-text-light' : item.selected && !isDark
                  }"
              >
            <ion-label>
              "{{ voiceWordString }} {{ item.index+1 }}" {{ item.displayName }} 
            </ion-label>
          </ion-item> 
        </ion-list>
        <div  :style="styleVoicesSelected">
          <ion-item>
            <ion-label>
              <b>{{ translateEnglishWords("Player") }}: </b>{{ playerDisplayName }}<br><b>{{ translateEnglishWords("Opponent") }}: </b>{{ opponentDisplayName }}
            </ion-label>
          </ion-item>
        </div>
        <ToolBar :style="styleToolbar" :elements="toolbarElements" :trigger="toolbarTrigger" @toolbar-click="toolbarClick" />
      </div>
    </ion-content>
  </ion-page>
  
</template>

<script setup>

  import { IonPage, IonContent,IonList,IonItem,IonLabel  } from '@ionic/vue';
  import { computed } from "@vue/reactivity"

  import { ref, watch } from 'vue'
  import ExplainTour from '@/components/ExplainTour.vue'
  import ToolBar from '@/components/ToolBar.vue'
  import { useRouter } from 'vue-router';
  import { useConnectionStore } from '@/stores/connectionStore';
  import { useWindowSizeStore } from '@/stores/windowSizeStore';

  import { buildStyleToolbar, buildStyleTopline,buildStyleMiddle,availableWidthHeight, scrollElement,
    calculateChessBoardRect, isScreenPortraitOrientation } from '@/composables/screenSize'

  import { doStartupCheck } from '@/composables/startupCheck.js'
  import TopLine from '@/components/TopLine.vue'
  import { parseWordsSaid , buildWordListFromChoiceList, parseNumberWords } from '@/composables/wordlistSupport.js'
  import { getListofVoices , voiceAddMicrophoneSupport } from '@/composables/voicesSupport.js'
  import { translateEnglishWords } from '@/composables/translation';

  const emit = defineEmits(['where-next','word-list','mic-is-muted'])

  const props = defineProps({
      wordsSaid : {
        type : String,
        default : ""
      },
      wordsSaidTrigger : {
        type : Number,
        default : 0
      }
    })

  const wordsSaid = computed({get() {return props.wordsSaid}})
  const wordsSaidTrigger = computed({get() {return props.wordsSaidTrigger}})

  const connectionStore = useConnectionStore()  
  const windowSizeStore = useWindowSizeStore()

  const isDark = ref(connectionStore.getDarkmode != "light")

  const microphoneIsMuted = ref(connectionStore.getMicrophoneIsMuted)
  emit("mic-is-muted",microphoneIsMuted.value)

  const translationLanguage = connectionStore.languageInUse
  
  const wordlistTrigger = ref(0)
  const wordlistVoices = "command scroll up down set player opponent voice explain done ignore mute listen one two three four five six seven eight nine ten eleven twelve thirteen fourteen fifteen sixteen seventeen eighteen nineteen twenty thirty forty fifty sixty seventy eighty ninety";
  const wordlist = ref(wordlistVoices)
  emit("word-list",wordlistVoices)

  const SHOW_VOICES = 0
  const SHOW_EXPLAINTOUR = 1
  const showState = ref(SHOW_VOICES)

  const TOOLBAR_SET_PLAYER = 0
  const TOOLBAR_SET_OPPONENT = 1
  const TOOLBAR_EXPLAINTOUR = 2

  const toolbarElements = ref([
    { index : TOOLBAR_SET_PLAYER, label : translateEnglishWords("Set Player") ,disabled : true},
    { index : TOOLBAR_SET_OPPONENT, label : translateEnglishWords("Set Opponent") ,disabled : true},
    { index : TOOLBAR_EXPLAINTOUR, label : translateEnglishWords("Explain") ,disabled : false},
  ])


  const toolbarTrigger = ref(0)
  const isLoading = ref(true)
  const currentSelectedVoice = ref(-1)
  var scrollTop = 0

  const selectVoicesTitle = ref(translateEnglishWords("Select Voices"))
  const voiceWordString = ref(translateEnglishWords("Voice"))

  const explainTourWordsSpoken = ref("")
  const explainTourScreen = ref("")
  const explainTourWordsSpokenTrigger = ref(0)

  const voicesItems = ref([])
  var voices = []
  var engineVoiceNumber = -1
  var playerVoiceNumber = -1

  const originalPlayerVoiceName = connectionStore.getPlayerVoiceName
  const originalPlayerVoiceLang = connectionStore.getPlayerVoiceLang
  const originalOpponentVoiceName = connectionStore.getOpponentVoiceName
  const originalOpponentVoiceLang = connectionStore.getOpponentVoiceLang

  const styleToolbar = ref(buildStyleToolbar(availableWidthHeight()))
  const styleTopline = ref(buildStyleTopline(availableWidthHeight()))
  const styleVoicesAvailable = ref(buildStyleMiddle(availableWidthHeight()))
  const styleVoicesSelected = ref(buildStyleMiddle(availableWidthHeight()))
  updateWidthHeights()

  //console.log("**** Original Opponent",originalOpponentVoiceName,originalOpponentVoiceLang)

  const playerDisplayName = ref("Default")
  const opponentDisplayName = ref("Default")

   watch(wordsSaidTrigger, (newVal,oldVal) => {
     //console.log('*********** words said ',props.wordsSaid)
     processWords(props.wordsSaid)
   })
  watch(() => windowSizeStore.getWindowSize, () => {
      //console.log('*********** Explorer -- window size changes detected')
      updateWidthHeights()
  })

  doStartupCheck(false,afterStartupCallbackRoutine)

  function afterStartupCallbackRoutine(stuffChanged) {
        getListofVoices(handleVoicesCallback)
  }

  function updateWidthHeights() {

    const heightOfSelected = 50
    const availableSpace = availableWidthHeight()

    styleToolbar.value = buildStyleToolbar(availableSpace)
    styleTopline.value = buildStyleTopline(availableSpace)
    styleVoicesAvailable.value = buildStyleMiddle(availableWidthHeight())
    styleVoicesSelected.value = buildStyleMiddle(availableWidthHeight())

    const availableHeight = (availableSpace.height - heightOfSelected)
    const selectedTop = (availableSpace.topIndent + availableSpace.height - heightOfSelected)
    styleVoicesAvailable.value.height = availableHeight + "px"
    styleVoicesSelected.value.top =  selectedTop+ "px"
    styleVoicesSelected.value.height = heightOfSelected + "px"
  }

  function handleVoicesCallback(voices1,playerNumber,engineNumber) {
    voices = voices1
    var index = 0
    for (var i=0; i < voices.length; i++) {
      var oneVoice = voices[i]
      if (!oneVoice.lang.startsWith(translationLanguage))
        continue;
      const country = oneVoice.lang.substring(3,5)
      var displayName = country + " - "

      // see if there is an "(English ...)" to remove
      const lastparen = oneVoice.name.lastIndexOf(")");
      const firstparen = oneVoice.name.indexOf("(")
      var partInParenthesis = ""
      if (firstparen >= 0 && lastparen >= 0) {
        partInParenthesis = oneVoice.name.substring(firstparen+1,lastparen)
        if (partInParenthesis.includes("English"))
          displayName += oneVoice.name.substring(0,firstparen)
        else
          displayName += oneVoice.name
      }
      else
        displayName += oneVoice.name

      //console.log("parts ",country,firstparen,lastparen,partInParenthesis)

      if (originalPlayerVoiceLang == oneVoice.lang && originalPlayerVoiceName == oneVoice.name)
        playerDisplayName.value = displayName

      if (originalOpponentVoiceLang == oneVoice.lang && originalOpponentVoiceName == oneVoice.name) {
        opponentDisplayName.value = displayName
        //console.log("found opponent",displayName)
      }
      
      oneVoice.selected = false
      oneVoice.index = index++
      oneVoice.displayName = displayName
      voicesItems.value.push(oneVoice)
    }
    //console.log("VOICE NUMBERS",playerNumber,engineNumber)
    //console.log("voices",voicesItems.value)
    playerVoiceNumber = playerNumber
    engineVoiceNumber = engineNumber
    isLoading.value = false
  }

  function saySample(voiceNumber) {
    let text = translateEnglishWords("rook , night , bishop , queen , king , pawn")

    const rate = 0.9
    var msg = new SpeechSynthesisUtterance()
    voiceAddMicrophoneSupport(msg)

    msg.rate = rate
    msg.voice = voicesItems.value[voiceNumber]
    msg.text = text
    window.speechSynthesis.speak(msg)
  }

  // START Explain Tour Routines

  function explainTourInitialize(errorText) {
    showState.value = SHOW_EXPLAINTOUR
    explainTourScreen.value = (errorText == null) ? "select_voices" : ("ERROR:" + errorText);
    wordlist.value = connectionStore.getExplainTourWords
    emit("word-list",wordlistVoices)
    wordlistTrigger.value++
  }

  function explainTourToolbarClick(whatClicked) {
    switch (whatClicked) {

      case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "done" :
        showState.value = SHOW_VOICES
        wordlist.value = wordlistVoices
        emit("word-list",wordlistVoices)
        wordlistTrigger.value++
        break
    }
  }

  // END Explain Tour Routines

  function toplineClick(whatClicked) {
    //console.log("topline",whatClicked)
    switch (whatClicked) {
      case "done" :
        processWords("command done")
        break;
      case "microphone" :
        microphoneIsMuted.value = !microphoneIsMuted.value 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(microphoneIsMuted.value)
        break

    }
  }

  function toolbarClick(arrayIndex) {
    switch (arrayIndex) {
      case TOOLBAR_SET_PLAYER :
        processWords("command set player")
        break
      case TOOLBAR_SET_OPPONENT :
        processWords("command set opponent")
        break
      case TOOLBAR_EXPLAINTOUR :
        processWords("command explain")
        break
    }
 
  }

  function onClick(index) {

    currentSelectedVoice.value = index
    //console.log("click on ",index)
    const voiceId = voicesItems.value[index].voiceId

    // remove any selected
    for (var i=0; i < voicesItems.value.length; i++)
      voicesItems.value[i].selected = false

    voicesItems.value[index].selected = true
    //console.log("after click", voicesItems.value)

    saySample(index)

    toolbarElements.value[TOOLBAR_SET_PLAYER].disabled = false
    toolbarElements.value[TOOLBAR_SET_OPPONENT].disabled = false
    toolbarTrigger.value++
  }

  function parseCommands(words,currentWordIndex) {
    
    //console.log("in parse commands",words,currentWordIndex)
    var currentWord = (words.length > currentWordIndex) ? words[currentWordIndex] : "xyz"
    var nextWord = (words.length > currentWordIndex+1) ? words[currentWordIndex+1] : "xyz"
    var nextNextWord = (words.length > currentWordIndex+2) ? words[currentWordIndex+2] : "xyz"

    for (var i = 0; i < words.length; i++) {
        if (currentWord == "ignore") {
            return ["ignore",""];
        }
        if (currentWord == "explain") {
            return ["explain",""]
        }
        if (currentWord == "done" || currentWord == "mute" || currentWord == "listen")
          return [currentWord,""]

        if (currentWord == "scroll") {
            if (nextWord == "up") {
                return ["scroll", "up"]
            }
            if (nextWord == "down") {
                return ["scroll", "down"]
            }
        }
        
        // add choices here
        if (currentWord == "set") {
          if (nextWord == "player" || nextWord == "opponent")
            return (["set",nextWord])
        }
        if (currentWord == "voice") {
          const voiceIndex = parseNumberWords(nextWord,nextNextWord)
          return ["voice", voiceIndex]
        }
       
        currentWord = nextWord;
        nextWord = nextNextWord;
        nextNextWord = (i < words.length - 3) ? words[i + 3] : "xyz";
    }
    return (["ignore",""]);
  }

  function processWords(text) {
    //console.log("text in engine Menu ",text)
    var returnArray = parseWordsSaid(text,parseCommands)
    //console.log("command found",returnArray)

    if (showState.value == SHOW_EXPLAINTOUR) {
      explainTourWordsSpoken.value = text
      explainTourWordsSpokenTrigger.value++
      return;
    }

    switch (returnArray[0]) {
      case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "scroll" :
        scrollElement("select-voices",returnArray[1])
        break
      case "voice" :
        const voiceIndex = returnArray[1] - 1
        if (voiceIndex < 0)
          break
        if (voiceIndex > voicesItems.value.length -1)
          break
        onClick(voiceIndex)
        break
      case "set" :
        if (currentSelectedVoice.value == -1)
          break
        var selectedVoice = voicesItems.value[currentSelectedVoice.value]
        //console.log("selected ",returnArray[1],selectedVoice)
        if (returnArray[1] == "player") {
          playerVoiceNumber = currentSelectedVoice.value
          playerDisplayName.value = selectedVoice.displayName
          connectionStore.setPlayerVoice(selectedVoice.name,selectedVoice.lang)
        }
        else
        if (returnArray[1] == "opponent") {
          engineVoiceNumber = currentSelectedVoice.value
          opponentDisplayName.value = selectedVoice.displayName
          connectionStore.setOpponentVoice(selectedVoice.name,selectedVoice.lang)
        }
        break
      case "explain" :
        explainTourInitialize(null)
        break
      case "done" :
        emit("where-next","/voicesmenu")
        //router.replace({ path: "/accountmenu"})
        break
    }
  }

</script>

<style scoped>

.selected-text-light {
  padding: 2px;
  border : solid 1px black;
  border-radius: 3px;
}

.selected-text-dark {
  padding: 2px;
  border : solid 1px #fff;
  border-radius: 3px;
}


</style>